import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { featureRequestApi } from '../../api/endpoints';
import { getApiErrorMessage, fetchFeatureRequestFileBlob } from '../../api/client';
import { notify } from '../../components/Notifier';
import { useAuth } from '../../auth/AuthContext';
import { ConfirmDialog } from '../Admin/ConfirmDialog';
import { SavedFileList } from '../../components/SavedFileList';
import { formatDateTime } from '../../utils/date';
import { FEATURE_REQUEST_STATUS_META } from './statusMeta';
import { RequestFormDialog } from './RequestFormDialog';
import { ConvertDialog } from './ConvertDialog';
import { RejectDialog } from './RejectDialog';
import { RequestCommentThread } from './RequestCommentThread';
import { LinkProjectDialog } from './LinkProjectDialog';
import { usePermission } from '../../auth/usePermission';
import type { FeatureRequest, FeatureRequestFile } from '../../types';

interface RequestDetailDialogProps {
  request: FeatureRequest;
  onClose: () => void;
  /** Gọi sau khi yêu cầu được sửa/xoá (để danh sách refresh). */
  onChanged: () => void;
}

/** Dialog chi tiết một yêu cầu chức năng: mô tả, ảnh, lý do từ chối, link chức năng đã chuyển. */
export function RequestDetailDialog({
  request: initialRequest,
  onClose,
  onChanged,
}: RequestDetailDialogProps): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState<FeatureRequest>(initialRequest);
  const [files, setFiles] = useState<FeatureRequestFile[]>(initialRequest.files ?? []);

  // `initialRequest` thường là một dòng của danh sách (GET /feature-requests),
  // vốn KHÔNG kèm `files` (chỉ có ở GET /feature-requests/:id — xem types/index.ts).
  // Tải lại chi tiết đầy đủ khi dialog mở để file đã đính kèm hiện đúng.
  useEffect(() => {
    let cancelled = false;
    void featureRequestApi.get(initialRequest.id).then(
      (full) => {
        if (cancelled) return;
        setRequest(full);
        setFiles(full.files ?? []);
      },
      (err) => {
        if (!cancelled) notify(getApiErrorMessage(err, 'Không thể tải chi tiết yêu cầu'), 'error');
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRequest.id]);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const meta = FEATURE_REQUEST_STATUS_META[request.status];
  const isOwnerWhileNew = user?.id === request.created_by && request.status === 'NEW';
  const isOwnerCreator = user?.id === request.created_by;
  const canConvert = usePermission('FEATURE_REQUEST_CONVERT');
  const canDeleteOthers = usePermission('FEATURE_REQUEST_DELETE');
  const canAct = canConvert && (request.status === 'NEW' || request.status === 'REVIEWING');
  const [convertOpen, setConvertOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [linkProjectOpen, setLinkProjectOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const canComment = usePermission('FEATURE_REQUEST_COMMENT');
  const canConfirmPerm = usePermission('FEATURE_REQUEST_CONFIRM');
  const isTerminalStatus = request.status === 'CONVERTED' || request.status === 'REJECTED';
  // Yêu cầu chưa gắn dự án: không có "khách hàng dự án" để đối chiếu, backend
  // chỉ cho chính người tạo xác nhận. Yêu cầu đã gắn dự án: backend kiểm tra
  // thuộc project_users — ở đây không lặp lại truy vấn đó, cứ hiện nút và để
  // backend là lưới chặn cuối (403 nếu không thuộc dự án).
  const canShowConfirmButton =
    canConfirmPerm &&
    !request.confirmed_at &&
    !isTerminalStatus &&
    (request.project_id !== null || isOwnerCreator);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const updated = await featureRequestApi.confirm(request.id);
      setRequest(updated);
      notify('Đã xác nhận yêu cầu', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xác nhận yêu cầu'), 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await featureRequestApi.remove(request.id);
      notify('Đã xoá yêu cầu', 'success');
      setDeleteOpen(false);
      onChanged();
      onClose();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá yêu cầu'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const goToFeature = () => {
    if (!request.converted_feature_id) return;
    navigate(`/features/${request.converted_feature_id}`);
    onClose();
  };

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6, wordBreak: 'break-word' }}>
          {request.title}
          <IconButton aria-label="Đóng" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={meta.label}
                color={meta.color === 'default' ? 'default' : meta.color}
                size="small"
                variant={meta.color === 'default' ? 'outlined' : 'filled'}
              />
              {request.project_name && (
                <Typography variant="body2" color="text.secondary">
                  {request.project_name}
                  {request.phase_name ? ` › ${request.phase_name}` : ''}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Tạo bởi {request.created_by_name ?? 'không rõ'} lúc {formatDateTime(request.created_at)}
            </Typography>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Mô tả mong muốn
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              color={request.description ? 'text.primary' : 'text.secondary'}
            >
              {request.description || 'Chưa có mô tả.'}
            </Typography>

            <SavedFileList files={files} fetchBlob={(f) => fetchFeatureRequestFileBlob(f.id)} />

            {request.status === 'REJECTED' && request.reject_reason && (
              <Alert severity="warning">Lý do từ chối: {request.reject_reason}</Alert>
            )}

            {request.status === 'CONVERTED' && request.converted_feature_name && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" startIcon={<LaunchIcon />} onClick={goToFeature}>
                    Mở
                  </Button>
                }
              >
                Đã chuyển thành chức năng <strong>{request.converted_feature_name}</strong>
                {request.converted_feature_project_name
                  ? ` — ${request.converted_feature_project_name}${
                      request.converted_feature_phase_name ? ` › ${request.converted_feature_phase_name}` : ''
                    }`
                  : ''}
              </Alert>
            )}

            {request.confirmed_at && (
              <Alert severity="info">
                Đã được {request.confirmed_by_name ?? 'khách hàng'} xác nhận lúc{' '}
                {formatDateTime(request.confirmed_at)}
              </Alert>
            )}

            {!request.project_id && !isTerminalStatus && (
              <Alert
                severity="warning"
                action={
                  isOwnerCreator && !isTerminalStatus ? (
                    <Button color="inherit" size="small" onClick={() => setLinkProjectOpen(true)}>
                      Gắn dự án
                    </Button>
                  ) : undefined
                }
              >
                Yêu cầu chưa gắn vào dự án nào
                {!request.confirmed_at ? ' — chỉ người tạo mới xác nhận được.' : '.'}
              </Alert>
            )}

            <Divider />
            <RequestCommentThread requestId={request.id} canComment={canComment} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1}>
            {(isOwnerWhileNew || canDeleteOthers) && (
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)}>
                Xoá
              </Button>
            )}
            {isOwnerWhileNew && (
              <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Sửa
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            {canShowConfirmButton && (
              <Button variant="outlined" disabled={confirming} onClick={() => void handleConfirm()}>
                {confirming ? 'Đang xác nhận…' : 'Xác nhận yêu cầu'}
              </Button>
            )}
            {canAct && (
              <>
                <Button color="error" onClick={() => setRejectOpen(true)}>
                  Từ chối
                </Button>
                <Button variant="contained" onClick={() => setConvertOpen(true)}>
                  Chuyển thành chức năng
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <RequestFormDialog
        open={editOpen}
        request={{ ...request, files }}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setRequest(saved);
          setEditOpen(false);
          onChanged();
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Xoá yêu cầu"
        message={`Xoá yêu cầu "${request.title}"?`}
        confirmLabel="Xoá"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      />

      <ConvertDialog
        open={convertOpen}
        request={request}
        onClose={() => setConvertOpen(false)}
        onConverted={() => {
          setConvertOpen(false);
          onChanged();
          onClose();
        }}
      />

      <RejectDialog
        open={rejectOpen}
        request={request}
        onClose={() => setRejectOpen(false)}
        onRejected={(updated) => {
          setRequest(updated);
          setRejectOpen(false);
          onChanged();
        }}
      />

      <LinkProjectDialog
        open={linkProjectOpen}
        request={request}
        onClose={() => setLinkProjectOpen(false)}
        onLinked={(updated) => {
          setRequest(updated);
          setLinkProjectOpen(false);
          onChanged();
        }}
      />
    </>
  );
}
